import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  GiftIcon,
  ShoppingCartIcon,
  ArrowRightIcon,
  TagIcon,
  SparklesIcon,
  CalendarIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

const BundleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loadCart } = useCart();
  
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchBundle();
  }, [id]);

  const fetchBundle = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/bundles/${id}`);
      setBundle(response.data);
    } catch (error) {
      setError('שגיאה בטעינת פרטי החבילה');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    try {
      const token = localStorage.getItem('token');
      const sessionId = localStorage.getItem('sessionId') || `session-${Date.now()}`;
      
      if (!localStorage.getItem('sessionId')) {
        localStorage.setItem('sessionId', sessionId);
      }
      
      await axios.post('/api/cart/bundle', 
        { 
          bundle_id: bundle.id, 
          quantity 
        },
        {
          headers: {
            'x-session-id': sessionId,
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );
      
      // Reload cart to update count
      await loadCart();
      
      toast.success('החבילה נוספה לעגלה בהצלחה! 🎁');
      
      // Navigate to cart after short delay to show the toast
      setTimeout(() => navigate('/cart'), 1000);
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בהוספת החבילה לעגלה');
    }
  };

  const calculateSavings = () => {
    if (!bundle?.regular_price || !bundle?.bundle_price) return 0;
    const regular = parseFloat(bundle.regular_price);
    const bundlePrice = parseFloat(bundle.bundle_price);
    return Math.round(((regular - bundlePrice) / regular) * 100);
  };

  const getTotalSavings = () => {
    if (!bundle?.regular_price || !bundle?.bundle_price) return 0;
    const regular = parseFloat(bundle.regular_price);
    const bundlePrice = parseFloat(bundle.bundle_price);
    return (regular - bundlePrice) * quantity;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="h-96 bg-gray-200 rounded"></div>
              <div>
                <div className="h-10 bg-gray-200 rounded mb-4"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !bundle) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error || 'חבילה לא נמצאה'}
          </div>
        </div>
      </div>
    );
  }

  const images = bundle.images || (bundle.bundle_image ? [bundle.bundle_image] : []);
  const savings = calculateSavings();
  const totalSavings = getTotalSavings();

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center text-sm mb-8" aria-label="Breadcrumb">
          <Link to="/" className="text-gray-500 hover:text-gray-700">
            דף הבית
          </Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/bundles" className="text-gray-500 hover:text-gray-700">
            חבילות מבצע
          </Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900">{bundle.title}</span>
        </nav>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
            {/* Images */}
            <div>
              {/* Main Image */}
              <div className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden mb-4">
                {images.length > 0 ? (
                  <img
                    src={`/api${images[selectedImage]}`}
                    alt={bundle.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <GiftIcon className="h-32 w-32 text-gray-400" />
                  </div>
                )}
                
                {/* Badges */}
                <div className="absolute top-4 right-4 flex flex-col gap-2">
                  {bundle.is_featured && (
                    <span className="px-3 py-1 bg-yellow-500 text-white text-sm font-bold rounded-full shadow-lg flex items-center">
                      <SparklesIcon className="h-4 w-4 ml-1" />
                      מומלץ
                    </span>
                  )}
                  {savings > 0 && (
                    <span className="px-3 py-1 bg-green-500 text-white text-lg font-bold rounded-full shadow-lg">
                      חסכון {savings}%
                    </span>
                  )}
                </div>
              </div>

              {/* Thumbnail Images */}
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 ${
                        selectedImage === index
                          ? 'border-primary-500'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={`/api${image}`}
                        alt={`${bundle.title} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                {bundle.title}
              </h1>

              {bundle.category && (
                <div className="flex items-center text-sm text-gray-500 mb-4">
                  <TagIcon className="h-4 w-4 ml-1" />
                  <span>{bundle.category.name}</span>
                </div>
              )}

              {bundle.description && (
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {bundle.description}
                </p>
              )}

              {/* Pricing */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                {bundle.regular_price && (
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-600">מחיר רגיל:</span>
                    <span className="text-xl text-gray-500 line-through">
                      ₪{parseFloat(bundle.regular_price).toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xl font-semibold text-gray-900">מחיר חבילה:</span>
                  <span className="text-4xl font-bold text-primary-600">
                    ₪{parseFloat(bundle.bundle_price).toFixed(2)}
                  </span>
                </div>
                {totalSavings > 0 && (
                  <div className="text-center py-3 bg-green-50 rounded-lg">
                    <span className="text-green-700 font-semibold">
                      חוסכים ₪{totalSavings.toFixed(2)}!
                    </span>
                  </div>
                )}
              </div>

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  כמות:
                </label>
                <div className="flex items-center space-x-4 space-x-reverse">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20 text-center border border-gray-300 rounded-lg py-2"
                    min="1"
                  />
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                className="w-full btn btn-primary btn-lg flex items-center justify-center mb-4"
              >
                <ShoppingCartIcon className="h-6 w-6 ml-2" />
                הוסף לעגלה - ₪{(parseFloat(bundle.bundle_price) * quantity).toFixed(2)}
              </button>

              {/* Features */}
              <div className="border-t pt-6 space-y-3">
                <div className="flex items-center text-gray-600">
                  <TruckIcon className="h-5 w-5 ml-2 text-primary-600" />
                  <span>משלוח חינם על הזמנות מעל ₪200</span>
                </div>
                {bundle.valid_until && (
                  <div className="flex items-center text-gray-600">
                    <CalendarIcon className="h-5 w-5 ml-2 text-primary-600" />
                    <span>
                      המבצע בתוקף עד:{' '}
                      {new Date(bundle.valid_until).toLocaleDateString('he-IL')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Products in Bundle */}
          {bundle.items && bundle.items.length > 0 && (
            <div className="border-t p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                מוצרים בחבילה ({bundle.items.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {bundle.items.map((item) => (
                  <div key={item.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0 overflow-hidden">
                        {item.product.images && item.product.images.length > 0 ? (
                          <img
                            src={`/api${item.product.images[0]}`}
                            alt={item.product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <TagIcon className="h-8 w-8 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {item.product.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          מק"ט: {item.product.sku}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">
                            כמות: {item.quantity}
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            ₪{item.product.price_b2c}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Back Button */}
        <div className="mt-8">
          <Link
            to="/bundles"
            className="inline-flex items-center text-primary-600 hover:text-primary-700"
          >
            <ArrowRightIcon className="h-5 w-5 ml-1" />
            חזור לכל החבילות
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BundleDetailPage;
